// node_modules
import { FC, useContext, useEffect, useState } from "react";
// Components
import { AsynchronousWrapper, DocumentHeaderSplitter, PartnerResult, SidebarFilterMenu, TitledTypedChild } from "Components";
// Contexts
import { CaseContext, TechnologyContext } from "Stores";
// Style
import styles from "./technologyPartners.module.scss";
// Types
import { TPartnerResultDTO, TTechnologyPartnerResultsDTO, TUseFetch } from "Types";
// Custom hooks
import { useFetch } from "Hooks";
// Enums
import { PartnerTypeEnum, TechnologyActionTypeEnum } from "Enums";
// Controllers
import { TechnologyControllerSingleton } from "Controllers";
// Interfaces
import { ISetTechnologyCountsAction } from "Interfaces";
import { LoggingConstants } from "Constants";
import { PendoHelperSingleton } from "Helpers";

export const TechnologyPartners: FC = () => {
    // State
    const [currentUserUniversityPartners, setCurrentUserUniversityPartners] = useState<TPartnerResultDTO[]>([]);
    const [currentUserInstitutePartners, setCurrentUserInstitutePartners] = useState<TPartnerResultDTO[]>([]);
    const [currentUserCompanyPartners, setCurrentUserCompanyPartners] = useState<TPartnerResultDTO[]>([]);
    const [coworkerUniversityPartners, setCoworkerUniversityPartners] = useState<TPartnerResultDTO[]>([]);
    const [coworkerInstitutePartners, setCoworkerInstitutePartners] = useState<TPartnerResultDTO[]>([]);
    const [coworkerCompanyPartners, setCoworkerCompanyPartners] = useState<TPartnerResultDTO[]>([]);

    // Contexts
    const { caseState } = useContext(CaseContext);
    const { technologyState, technologyDispatch } = useContext(TechnologyContext);

    // Const
    const { isLoading, didError, errorMessage, fetchedData }: TUseFetch<TTechnologyPartnerResultsDTO> = useFetch(`api/technologies/${caseState.selectedTechnologyId}/partners`);

    // Logic
    useEffect(() => {
        if (!fetchedData) return;
        setPartnerResults(fetchedData);
    }, [fetchedData]);

    // Logging
    useEffect(() => {
        // Log the fetching of the partners
        PendoHelperSingleton.trackEventWithProperties(`${LoggingConstants.DYNAMIC_DELIVERY}TechnologyGetPartners`, {
            technologyId: caseState.selectedTechnologyId
        });
    }, [caseState.selectedTechnologyId]);

    const handleOrganizationHasBeenUnbookmarked = async (unbookmarkOnEntityId: string): Promise<void> => {
        if (caseState.selectedOriginalTechnologyId &&
            caseState.selectedOriginalTechnologyId.toString() === unbookmarkOnEntityId) {
            await updateTechnologyPartnerResultsAsync();
        }
    };

    const handleUnderneathDocumentHasBeenUnbookmarked = async (unbookmarkOnEntityId: string): Promise<void> => {
        if (caseState.selectedOriginalTechnologyId &&
        caseState.selectedOriginalTechnologyId.toString() === unbookmarkOnEntityId && caseState.selectedTechnologyId) {
            await updateSelectedTechnologyCounts();
        }
    };

    const handleUnderneathDocumentHasBeenBookmarked = async (bookmarkOnEntityId: string): Promise<void> => {
        if (caseState.selectedOriginalTechnologyId &&
        caseState.selectedOriginalTechnologyId.toString() === bookmarkOnEntityId) {
            await updateSelectedTechnologyCounts();
        }
    };

    const updateSelectedTechnologyCounts = async (): Promise<void> => {
        if (caseState.selectedTechnologyId) {
            // update counts in the TechnologyContext (in order to re-render components which use this counts)
            technologyState.counts = await TechnologyControllerSingleton.getTechnologyCountsAsync(caseState.selectedTechnologyId);
            technologyDispatch({ type: TechnologyActionTypeEnum.SetTechnologyCounts, counts: technologyState.counts } as ISetTechnologyCountsAction);
        }
    };

    const updateTechnologyPartnerResultsAsync = async (): Promise<void> => {
        if (!caseState.selectedTechnologyId) return;

        const newPartnerResults: TTechnologyPartnerResultsDTO = await TechnologyControllerSingleton.getTechnologyPartnerResultsAsync(caseState.selectedTechnologyId);

        // increase the partner count in the TechnologyContext (in order to re-render components which use this number)
        technologyState.counts.partnerCount = newPartnerResults.currentUserPartnerResults.length + newPartnerResults.coworkerPartnerResults.length;
        technologyDispatch({ type: TechnologyActionTypeEnum.SetTechnologyCounts, counts: technologyState.counts } as ISetTechnologyCountsAction);

        setPartnerResults(newPartnerResults);
    };

    const setPartnerResults = (newPartnerResults: TTechnologyPartnerResultsDTO): void => {
        if (!newPartnerResults) return;
        setCurrentUserUniversityPartners(Array.from(newPartnerResults.currentUserPartnerResults.filter(partnerResult => partnerResult.partnerType === PartnerTypeEnum.University)));
        setCurrentUserInstitutePartners(Array.from(newPartnerResults.currentUserPartnerResults.filter(partnerResult => partnerResult.partnerType === PartnerTypeEnum.ResearchInstitute)));
        setCurrentUserCompanyPartners(Array.from(newPartnerResults.currentUserPartnerResults.filter(partnerResult => partnerResult.partnerType === PartnerTypeEnum.Company)));
        setCoworkerUniversityPartners(Array.from(newPartnerResults.coworkerPartnerResults.filter(partnerResult => partnerResult.partnerType === PartnerTypeEnum.University)));
        setCoworkerInstitutePartners(Array.from(newPartnerResults.coworkerPartnerResults.filter(partnerResult => partnerResult.partnerType === PartnerTypeEnum.ResearchInstitute)));
        setCoworkerCompanyPartners(Array.from(newPartnerResults.coworkerPartnerResults.filter(partnerResult => partnerResult.partnerType === PartnerTypeEnum.Company)));
    };

    return (
        <div className={styles.technologyPartnersContainer}>
            {(currentUserUniversityPartners &&
                coworkerUniversityPartners &&
                currentUserInstitutePartners &&
                coworkerInstitutePartners &&
                currentUserCompanyPartners &&
                coworkerCompanyPartners) ?
                <AsynchronousWrapper didError={didError} errorMessage={errorMessage} isLoading={isLoading} LoaderComponent={null}>
                    <SidebarFilterMenu selectedFilterName="university">
                        {caseState.isDynamicDelivery || currentUserUniversityPartners.length + coworkerUniversityPartners.length > 0 ?
                            <TitledTypedChild title={`University (${currentUserUniversityPartners.length + coworkerUniversityPartners.length})`} typeName="university">
                                <div className={[styles.technologyPartnersContentContainer, styles.scrollbarCustomThin].join(" ")}>
                                    {coworkerUniversityPartners.length >= 1 ?
                                        <DocumentHeaderSplitter title="Coworker added universities" />
                                        :
                                        null
                                    }
                                    {coworkerUniversityPartners.map((partner) => {
                                        partner.isBookmarked = true;
                                        return (
                                            <PartnerResult
                                                partner={partner}
                                                key={partner.name}
                                                handleOrganizationHasBeenUnbookmarked={handleOrganizationHasBeenUnbookmarked}
                                                handleUnderneathDocumentHasBeenUnbookmarked={handleUnderneathDocumentHasBeenUnbookmarked} 
                                                handleUnderneathDocumentHasBeenBookmarked={handleUnderneathDocumentHasBeenBookmarked} />
                                        );
                                    })}
                                    {currentUserUniversityPartners.length >= 1 ?
                                        <DocumentHeaderSplitter title="User added universities" />
                                        :
                                        null
                                    }
                                    {currentUserUniversityPartners.map((partner) => {
                                        partner.isBookmarked = true;
                                        return (
                                            <PartnerResult
                                                partner={partner}
                                                key={partner.name}
                                                handleOrganizationHasBeenUnbookmarked={handleOrganizationHasBeenUnbookmarked}
                                                handleUnderneathDocumentHasBeenUnbookmarked={handleUnderneathDocumentHasBeenUnbookmarked} 
                                                handleUnderneathDocumentHasBeenBookmarked={handleUnderneathDocumentHasBeenBookmarked} />
                                        );
                                    })}
                                </div>
                            </TitledTypedChild>
                            : null }
                        {caseState.isDynamicDelivery || currentUserInstitutePartners.length + coworkerInstitutePartners.length > 0 ?
                            <TitledTypedChild title={`Institute (${currentUserInstitutePartners.length + coworkerInstitutePartners.length})`} typeName="institute">
                                <div className={[styles.technologyPartnersContentContainer, styles.scrollbarCustomThin].join(" ")}>
                                    {coworkerInstitutePartners.length >= 1 ?
                                        <DocumentHeaderSplitter title="Coworker added institutes" />
                                        :
                                        null
                                    }
                                    {coworkerInstitutePartners.map((partner) => {
                                        partner.isBookmarked = true;
                                        return (
                                            <PartnerResult
                                                partner={partner}
                                                key={partner.name}
                                                handleOrganizationHasBeenUnbookmarked={handleOrganizationHasBeenUnbookmarked}
                                                handleUnderneathDocumentHasBeenUnbookmarked={handleUnderneathDocumentHasBeenUnbookmarked} 
                                                handleUnderneathDocumentHasBeenBookmarked={handleUnderneathDocumentHasBeenBookmarked} />
                                        );
                                    })}
                                    {currentUserInstitutePartners.length >= 1 ?
                                        <DocumentHeaderSplitter title="User added institutes" />
                                        :
                                        null
                                    }
                                    {currentUserInstitutePartners.map((partner) => {
                                        partner.isBookmarked = true;
                                        return (
                                            <PartnerResult
                                                partner={partner}
                                                key={partner.name}
                                                handleOrganizationHasBeenUnbookmarked={handleOrganizationHasBeenUnbookmarked}
                                                handleUnderneathDocumentHasBeenUnbookmarked={handleUnderneathDocumentHasBeenUnbookmarked} 
                                                handleUnderneathDocumentHasBeenBookmarked={handleUnderneathDocumentHasBeenBookmarked} />
                                        );
                                    })}
                                </div>
                            </TitledTypedChild>
                            : null }
                        {caseState.isDynamicDelivery || currentUserCompanyPartners.length + coworkerCompanyPartners.length > 0 ?
                            <TitledTypedChild title={`Company (${currentUserCompanyPartners.length + coworkerCompanyPartners.length})`} typeName="company">
                                <div className={[styles.technologyPartnersContentContainer, styles.scrollbarCustomThin].join(" ")}>
                                    {coworkerCompanyPartners.length >= 1 ?
                                        <DocumentHeaderSplitter title="Coworker added companies" />
                                        :
                                        null
                                    }
                                    {coworkerCompanyPartners.map((partner) => {
                                        partner.isBookmarked = true;
                                        return (
                                            <PartnerResult
                                                partner={partner}
                                                key={partner.name}
                                                handleOrganizationHasBeenUnbookmarked={handleOrganizationHasBeenUnbookmarked}
                                                handleUnderneathDocumentHasBeenUnbookmarked={handleUnderneathDocumentHasBeenUnbookmarked} 
                                                handleUnderneathDocumentHasBeenBookmarked={handleUnderneathDocumentHasBeenBookmarked} />
                                        );
                                    })}
                                    {currentUserCompanyPartners.length >= 1 ?
                                        <DocumentHeaderSplitter title="User added companies" />
                                        :
                                        null
                                    }
                                    {currentUserCompanyPartners.map((partner) => {
                                        partner.isBookmarked = true;
                                        return (
                                            <PartnerResult
                                                partner={partner}
                                                key={partner.name}
                                                handleOrganizationHasBeenUnbookmarked={handleOrganizationHasBeenUnbookmarked}
                                                handleUnderneathDocumentHasBeenUnbookmarked={handleUnderneathDocumentHasBeenUnbookmarked} 
                                                handleUnderneathDocumentHasBeenBookmarked={handleUnderneathDocumentHasBeenBookmarked} />
                                        );
                                    })}
                                </div>
                            </TitledTypedChild>
                            : null }
                    </SidebarFilterMenu>
                </AsynchronousWrapper>
                :
                null
            }
        </div>
    );
};
